import React, { useState, useEffect, useRef, useCallback } from 'react';
import Matter from 'matter-js';
import Modal from 'react-modal';
import './index.css';

// Custom 3D circular fruit drawing functions
const drawCherry = (ctx, x, y, size) => {
  ctx.save();
  ctx.translate(x, y);
  
  // Perfect circle with cherry colors and 3D effect
  const gradient = ctx.createRadialGradient(-size*0.3, -size*0.3, 0, 0, 0, size);
  gradient.addColorStop(0, '#ff9a9e');
  gradient.addColorStop(0.3, '#ff6b9d');
  gradient.addColorStop(0.7, '#e55982');
  gradient.addColorStop(1, '#c44569');
  
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.fill();
  
  // 3D highlight
  const highlight = ctx.createRadialGradient(-size*0.4, -size*0.4, 0, -size*0.4, -size*0.4, size*0.6);
  highlight.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
  highlight.addColorStop(0.5, 'rgba(255, 255, 255, 0.3)');
  highlight.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  ctx.fillStyle = highlight;
  ctx.beginPath();
  ctx.arc(-size*0.2, -size*0.2, size*0.4, 0, Math.PI * 2);
  ctx.fill();
  
  // Small stem indicator
  ctx.fillStyle = '#27ae60';
  ctx.beginPath();
  ctx.arc(0, -size * 0.7, size * 0.1, 0, Math.PI * 2);
  ctx.fill();
  
  ctx.restore();
};

const drawStrawberry = (ctx, x, y, size) => {
  ctx.save();
  ctx.translate(x, y);
  
  // Perfect circle with strawberry colors
  const gradient = ctx.createRadialGradient(-size*0.2, -size*0.3, 0, 0, 0, size);
  gradient.addColorStop(0, '#ff7675');
  gradient.addColorStop(0.3, '#e17055');
  gradient.addColorStop(0.7, '#d63031');
  gradient.addColorStop(1, '#a4161a');
  
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.fill();
  
  // 3D highlight
  const highlight = ctx.createRadialGradient(-size*0.3, -size*0.3, 0, -size*0.3, -size*0.3, size*0.5);
  highlight.addColorStop(0, 'rgba(255, 255, 255, 0.6)');
  highlight.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  ctx.fillStyle = highlight;
  ctx.beginPath();
  ctx.arc(-size*0.2, -size*0.2, size*0.3, 0, Math.PI * 2);
  ctx.fill();
  
  // Small seed dots
  ctx.fillStyle = '#fff3cd';
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const radius = size * 0.6;
    const seedX = Math.cos(angle) * radius;
    const seedY = Math.sin(angle) * radius;
    ctx.beginPath();
    ctx.arc(seedX, seedY, size * 0.05, 0, Math.PI * 2);
    ctx.fill();
  }
  
  ctx.restore();
};

const drawOrange = (ctx, x, y, size) => {
  ctx.save();
  ctx.translate(x, y);
  
  // Perfect circle with orange colors
  const gradient = ctx.createRadialGradient(-size*0.3, -size*0.3, 0, 0, 0, size);
  gradient.addColorStop(0, '#ffb347');
  gradient.addColorStop(0.3, '#ffa502');
  gradient.addColorStop(0.7, '#ff6348');
  gradient.addColorStop(1, '#e55039');
  
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.fill();
  
  // 3D highlight
  const highlight = ctx.createRadialGradient(-size*0.4, -size*0.4, 0, -size*0.4, -size*0.4, size*0.6);
  highlight.addColorStop(0, 'rgba(255, 255, 255, 0.7)');
  highlight.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  ctx.fillStyle = highlight;
  ctx.beginPath();
  ctx.arc(-size*0.25, -size*0.25, size*0.35, 0, Math.PI * 2);
  ctx.fill();
  
  // Orange texture dots
  ctx.fillStyle = 'rgba(255, 102, 72, 0.3)';
  for (let i = 0; i < 15; i++) {
    const angle = Math.random() * Math.PI * 2;
    const radius = Math.random() * size * 0.7;
    const dotX = Math.cos(angle) * radius;
    const dotY = Math.sin(angle) * radius;
    ctx.beginPath();
    ctx.arc(dotX, dotY, size * 0.03, 0, Math.PI * 2);
    ctx.fill();
  }
  
  ctx.restore();
};

const drawKiwi = (ctx, x, y, size) => {
  ctx.save();
  ctx.translate(x, y);
  
  // Perfect circle - brown outer skin
  const outerGradient = ctx.createRadialGradient(-size*0.3, -size*0.3, 0, 0, 0, size);
  outerGradient.addColorStop(0, '#a0826d');
  outerGradient.addColorStop(0.3, '#8d6e63');
  outerGradient.addColorStop(0.7, '#6d4c41');
  outerGradient.addColorStop(1, '#3e2723');
  
  ctx.fillStyle = outerGradient;
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.fill();
  
  // Inner green circle
  const innerGradient = ctx.createRadialGradient(-size*0.2, -size*0.2, 0, 0, 0, size*0.7);
  innerGradient.addColorStop(0, '#a8e6cf');
  innerGradient.addColorStop(0.3, '#7bed9f');
  innerGradient.addColorStop(0.7, '#2ed573');
  innerGradient.addColorStop(1, '#27ae60');
  
  ctx.fillStyle = innerGradient;
  ctx.beginPath();
  ctx.arc(0, 0, size * 0.7, 0, Math.PI * 2);
  ctx.fill();
  
  // Center white circle
  ctx.fillStyle = '#f8f9fa';
  ctx.beginPath();
  ctx.arc(0, 0, size * 0.15, 0, Math.PI * 2);
  ctx.fill();
  
  // Seeds in circular pattern
  ctx.fillStyle = '#2c3e50';
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const seedRadius = size * 0.35;
    const seedX = Math.cos(angle) * seedRadius;
    const seedY = Math.sin(angle) * seedRadius;
    ctx.beginPath();
    ctx.arc(seedX, seedY, size * 0.04, 0, Math.PI * 2);
    ctx.fill();
  }
  
  // 3D highlight
  const highlight = ctx.createRadialGradient(-size*0.4, -size*0.4, 0, -size*0.4, -size*0.4, size*0.6);
  highlight.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
  highlight.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  ctx.fillStyle = highlight;
  ctx.beginPath();
  ctx.arc(-size*0.25, -size*0.25, size*0.3, 0, Math.PI * 2);
  ctx.fill();
  
  ctx.restore();
};

const drawApple = (ctx, x, y, size) => {
  ctx.save();
  ctx.translate(x, y);
  
  // Perfect circle with apple colors
  const gradient = ctx.createRadialGradient(-size*0.3, -size*0.3, 0, 0, 0, size);
  gradient.addColorStop(0, '#ff7675');
  gradient.addColorStop(0.3, '#e84393');
  gradient.addColorStop(0.7, '#d63031');
  gradient.addColorStop(1, '#74b9ff');
  
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.fill();
  
  // 3D highlight
  const highlight = ctx.createRadialGradient(-size*0.4, -size*0.4, 0, -size*0.4, -size*0.4, size*0.7);
  highlight.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
  highlight.addColorStop(0.6, 'rgba(255, 255, 255, 0.2)');
  highlight.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  ctx.fillStyle = highlight;
  ctx.beginPath();
  ctx.arc(-size*0.3, -size*0.3, size*0.4, 0, Math.PI * 2);
  ctx.fill();
  
  // Small leaf indicator
  ctx.fillStyle = '#27ae60';
  ctx.beginPath();
  ctx.arc(size*0.3, -size*0.6, size*0.1, 0, Math.PI * 2);
  ctx.fill();
  
  ctx.restore();
};

const drawWatermelon = (ctx, x, y, size) => {
  ctx.save();
  ctx.translate(x, y);
  
  // Perfect circle with watermelon colors
  const gradient = ctx.createRadialGradient(-size*0.3, -size*0.3, 0, 0, 0, size);
  gradient.addColorStop(0, '#55efc4');
  gradient.addColorStop(0.3, '#00b894');
  gradient.addColorStop(0.7, '#00a085');
  gradient.addColorStop(1, '#00695c');
  
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.fill();
  
  // Dark green stripe pattern
  ctx.strokeStyle = '#00695c';
  ctx.lineWidth = size * 0.06;
  for (let i = 0; i < 6; i++) {
    const angle = (i / 6) * Math.PI * 2;
    const x1 = Math.cos(angle) * size * 0.3;
    const y1 = Math.sin(angle) * size * 0.3;
    const x2 = Math.cos(angle) * size * 0.8;
    const y2 = Math.sin(angle) * size * 0.8;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  
  // 3D highlight
  const highlight = ctx.createRadialGradient(-size*0.4, -size*0.4, 0, -size*0.4, -size*0.4, size*0.7);
  highlight.addColorStop(0, 'rgba(255, 255, 255, 0.6)');
  highlight.addColorStop(0.5, 'rgba(255, 255, 255, 0.2)');
  highlight.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  ctx.fillStyle = highlight;
  ctx.beginPath();
  ctx.arc(-size*0.3, -size*0.3, size*0.4, 0, Math.PI * 2);
  ctx.fill();
  
  // Small seeds
  ctx.fillStyle = '#2c3e50';
  for (let i = 0; i < 6; i++) {
    const angle = (i / 6) * Math.PI * 2;
    const seedRadius = size * 0.5;
    const seedX = Math.cos(angle) * seedRadius;
    const seedY = Math.sin(angle) * seedRadius;
    ctx.beginPath();
    ctx.arc(seedX, seedY, size * 0.06, 0, Math.PI * 2);
    ctx.fill();
  }
  
  ctx.restore();
};

const drawLemon = (ctx, x, y, size) => {
  ctx.save();
  ctx.translate(x, y);
  
  // Perfect circle with lemon colors
  const gradient = ctx.createRadialGradient(-size*0.3, -size*0.3, 0, 0, 0, size);
  gradient.addColorStop(0, '#fffa65');
  gradient.addColorStop(0.3, '#ffd32a');
  gradient.addColorStop(0.7, '#f39801');
  gradient.addColorStop(1, '#e67e22');
  
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.fill();
  
  // 3D highlight
  const highlight = ctx.createRadialGradient(-size*0.4, -size*0.4, 0, -size*0.4, -size*0.4, size*0.6);
  highlight.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
  highlight.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  ctx.fillStyle = highlight;
  ctx.beginPath();
  ctx.arc(-size*0.3, -size*0.3, size*0.3, 0, Math.PI * 2);
  ctx.fill();
  
  // Lemon texture bumps
  ctx.fillStyle = 'rgba(243, 152, 1, 0.3)';
  for (let i = 0; i < 12; i++) {
    const angle = Math.random() * Math.PI * 2;
    const radius = Math.random() * size * 0.6;
    const dotX = Math.cos(angle) * radius;
    const dotY = Math.sin(angle) * radius;
    ctx.beginPath();
    ctx.arc(dotX, dotY, size * 0.04, 0, Math.PI * 2);
    ctx.fill();
  }
  
  ctx.restore();
};

const drawPineapple = (ctx, x, y, size) => {
  ctx.save();
  ctx.translate(x, y);
  
  // Perfect circle with pineapple colors
  const gradient = ctx.createRadialGradient(-size*0.3, -size*0.3, 0, 0, 0, size);
  gradient.addColorStop(0, '#f39c12');
  gradient.addColorStop(0.3, '#e67e22');
  gradient.addColorStop(0.7, '#d35400');
  gradient.addColorStop(1, '#a04000');
  
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.fill();
  
  // Diamond pattern in circle
  ctx.strokeStyle = '#d35400';
  ctx.lineWidth = size * 0.03;
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const x1 = Math.cos(angle) * size * 0.4;
    const y1 = Math.sin(angle) * size * 0.4;
    const x2 = Math.cos(angle + Math.PI/8) * size * 0.6;
    const y2 = Math.sin(angle + Math.PI/8) * size * 0.6;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  
  // Crown indicator
  ctx.fillStyle = '#27ae60';
  ctx.beginPath();
  ctx.arc(0, -size * 0.7, size * 0.2, 0, Math.PI * 2);
  ctx.fill();
  
  // 3D highlight
  const highlight = ctx.createRadialGradient(-size*0.4, -size*0.4, 0, -size*0.4, -size*0.4, size*0.6);
  highlight.addColorStop(0, 'rgba(255, 255, 255, 0.6)');
  highlight.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  ctx.fillStyle = highlight;
  ctx.beginPath();
  ctx.arc(-size*0.3, -size*0.3, size*0.3, 0, Math.PI * 2);
  ctx.fill();
  
  ctx.restore();
};

// Fruit definitions with better visibility - scaled for optimal gameplay
const FRUITS = [
  { renderer: 'cherry', size: 15, value: 1, color: '#ff4757' },        // Small red cherry (increased from 7px for visibility)
  { renderer: 'strawberry', size: 21, value: 2, color: '#e74c3c' },   // Strawberry with seeds (15 * 1.4 = 21)
  { renderer: 'lemon', size: 29, value: 4, color: '#f1c40f' },        // Yellow oval lemon (21 * 1.4 = 29.4)
  { renderer: 'kiwi', size: 41, value: 8, color: '#4caf50' },         // Brown/green kiwi (29 * 1.4 = 40.6)
  { renderer: 'orange', size: 57, value: 16, color: '#ff9800' },      // Orange with texture (41 * 1.4 = 57.4)
  { renderer: 'pineapple', size: 80, value: 32, color: '#f39c12' },   // Pineapple with crown (57 * 1.4 = 79.8)
  { renderer: 'apple', size: 112, value: 64, color: '#f44336' },      // Red apple with leaf (80 * 1.4 = 112)
  { renderer: 'watermelon', size: 157, value: 128, color: '#4caf50' } // Large watermelon (112 * 1.4 = 156.8)
];

function App() {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const renderRef = useRef(null);
  const worldRef = useRef(null);  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    // localStorage'dan high score'u oku
    const savedHighScore = localStorage.getItem('fruitMergeHighScore');
    return savedHighScore ? parseInt(savedHighScore, 10) : 0;
  });  const [nextFruit, setNextFruit] = useState(0);
  const [dropPosition, setDropPosition] = useState(187.5);  // Center of 375px width
  const [isGameOver, setIsGameOver] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);const fruitBodies = useRef(new Map());
  const canDrop = useRef(true);
  // Custom render loop for drawing fruits
  const animationIdRef = useRef(null);
  
  // Score değiştiğinde high score'u kontrol et ve güncelle
  useEffect(() => {
    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('fruitMergeHighScore', score.toString());
    }
  }, [score, highScore]);
  
  const customRender = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !engineRef.current) {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      return;
    }
    
    const ctx = canvas.getContext('2d');
    
    // Clear canvas overlay (for custom fruit drawings)
    ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Draw all fruits with custom renderers and animations
    engineRef.current.world.bodies.forEach(body => {
      const fruitData = fruitBodies.current.get(body.id);
      if (fruitData) {
        const { x, y } = body.position;
        const { size, renderer, animationScale = 1.0, isBouncing = false } = fruitData;
        
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(body.angle);
        
        // Apply animation scale
        const currentScale = animationScale;
        ctx.scale(currentScale, currentScale);
        
        // Add bounce effect for merged fruits
        if (isBouncing) {
          const bounce = Math.sin(Date.now() * 0.02) * 0.1 + 1;
          ctx.scale(bounce, bounce);
        }
          // Draw fruit based on renderer type
        switch (renderer) {
          case 'cherry':
            drawCherry(ctx, 0, 0, size);
            break;
          case 'strawberry':
            drawStrawberry(ctx, 0, 0, size);
            break;
          case 'lemon':
            drawLemon(ctx, 0, 0, size);
            break;
          case 'orange':
            drawOrange(ctx, 0, 0, size);
            break;
          case 'kiwi':
            drawKiwi(ctx, 0, 0, size);
            break;
          case 'pineapple':
            drawPineapple(ctx, 0, 0, size);
            break;
          case 'apple':
            drawApple(ctx, 0, 0, size);
            break;
          case 'watermelon':
            drawWatermelon(ctx, 0, 0, size);
            break;
          default:
            // Fallback circle with 3D effect
            const gradient = ctx.createRadialGradient(-size*0.3, -size*0.3, 0, 0, 0, size);
            gradient.addColorStop(0, fruitData.color);
            gradient.addColorStop(0.7, fruitData.color);
            gradient.addColorStop(1, '#000');
            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(0, 0, size, 0, Math.PI * 2);
            ctx.fill();
        }
        
        ctx.restore();
      }
    });
    
    // Continue the animation loop
    animationIdRef.current = requestAnimationFrame(customRender);
  }, []);
  // Initialize Matter.js physics engine
  const initPhysics = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;    // Fixed mobile game dimensions with increased height
    const width = canvas.width = 375;  // iPhone SE width
    const height = canvas.height = 800; // Increased height for more play area

    // Create engine
    const engine = Matter.Engine.create();
    engineRef.current = engine;
    worldRef.current = engine.world;    // Set more realistic gravity and air resistance
    engine.world.gravity.y = 1.0;      // Slightly reduced gravity for better rolling
    engine.world.gravity.scale = 0.001;

    // Create renderer
    const render = Matter.Render.create({
      element: canvas.parentElement,
      canvas: canvas,
      engine: engine,
      options: {
        width: width,
        height: height,
        wireframes: false,
        background: 'transparent',
        showVelocity: false,
        showAngleIndicator: false,
        showDebug: false
      }
    });
    renderRef.current = render;    // Create ground and walls with better positioning
    const ground = Matter.Bodies.rectangle(width / 2, height - 25, width, 50, {
      isStatic: true,
      friction: 0.1,       // Very low ground friction for easy rolling
      restitution: 0.1,    // Very low bounce on ground - mostly just rolling
      render: { fillStyle: '#8b4513' }
    });

    const leftWall = Matter.Bodies.rectangle(12.5, height / 2, 25, height, {
      isStatic: true,
      friction: 0.1,       // Very low wall friction
      restitution: 0.2,    // Low bounce on walls
      render: { fillStyle: '#8b4513' }
    });

    const rightWall = Matter.Bodies.rectangle(width - 12.5, height / 2, 25, height, {
      isStatic: true,
      friction: 0.1,       // Very low wall friction
      restitution: 0.2,    // Low bounce on walls
      render: { fillStyle: '#8b4513' }
    });// Add drop line (invisible physics body)
    const dropLine = Matter.Bodies.rectangle(width / 2, 150, width - 50, 5, {
      isStatic: true,
      isSensor: true,
      render: { fillStyle: 'transparent' }
    });

    Matter.World.add(engine.world, [ground, leftWall, rightWall, dropLine]);    // Handle collisions for merging
    const mergedPairs = new Set(); // Track merged pairs to prevent duplicate merges
    
    Matter.Events.on(engine, 'collisionStart', (event) => {
      event.pairs.forEach((pair) => {
        const { bodyA, bodyB } = pair;
        
        // Create unique pair ID to prevent duplicate merges
        const pairId = [bodyA.id, bodyB.id].sort().join('-');
        if (mergedPairs.has(pairId)) return;
        
        // Check if both bodies are fruits
        const fruitA = fruitBodies.current.get(bodyA.id);
        const fruitB = fruitBodies.current.get(bodyB.id);
        
        if (fruitA && fruitB && fruitA.value === fruitB.value && fruitA.value < 128) {
          // Mark this pair as merged
          mergedPairs.add(pairId);
          
          // Merge fruits after a short delay
          setTimeout(() => {
            // Double check bodies still exist
            if (!fruitBodies.current.has(bodyA.id) || !fruitBodies.current.has(bodyB.id)) {
              mergedPairs.delete(pairId);
              return;
            }
            
            const newValue = fruitA.value * 2;
            const newFruit = FRUITS.find(f => f.value === newValue);
            
            if (newFruit) {
              // Calculate position between the two fruits
              const x = (bodyA.position.x + bodyB.position.x) / 2;
              const y = (bodyA.position.y + bodyB.position.y) / 2;
              
              // Remove old fruits
              Matter.World.remove(engine.world, [bodyA, bodyB]);
              fruitBodies.current.delete(bodyA.id);
              fruitBodies.current.delete(bodyB.id);
                // Create new merged fruit with animation
              createFruit(x, y, newFruit, engine.world, true);
              
              // Update score
              setScore(prev => prev + newValue);
            }
            
            // Remove from merged pairs after processing
            mergedPairs.delete(pairId);
          }, 50);
        }
      });
    });// Start render
    Matter.Render.run(render);
    Matter.Engine.run(engine);
    
    // Start custom fruit rendering
    customRender();    return { engine, render };
  }, [customRender]);  // Create a fruit body with merge animation
  const createFruit = (x, y, fruit, world, isMerged = false) => {
    // Check if world still exists
    if (!world || !fruit) return null;    const body = Matter.Bodies.circle(x, y, fruit.size, {
      restitution: 0.2,    // Much lower bounce - just enough to feel natural
      friction: 0.1,       // Much lower friction to allow easy rolling
      frictionAir: 0.005,  // Very low air resistance
      frictionStatic: 0.1, // Very low static friction when at rest
      density: 0.002,      // Slightly increased density for better momentum
      render: {
        fillStyle: 'transparent' // We'll draw custom fruits on canvas
      }
    });

    // Store fruit data with animation info
    const fruitData = {
      ...fruit,
      animationScale: isMerged ? 0.3 : 1.0, // Start small if merged
      targetScale: 1.0,
      animationSpeed: isMerged ? 0.15 : 0,
      isBouncing: isMerged
    };
    
    fruitBodies.current.set(body.id, fruitData);
    
    try {
      Matter.World.add(world, body);
    } catch (error) {
      console.warn('Failed to add fruit to world:', error);
      fruitBodies.current.delete(body.id);
      return null;
    }

    // If it's a merged fruit, animate the pop effect
    if (isMerged) {
      const animateGrowth = () => {
        const currentFruit = fruitBodies.current.get(body.id);
        if (!currentFruit) return;
        
        if (currentFruit.animationScale < currentFruit.targetScale) {
          currentFruit.animationScale += currentFruit.animationSpeed;
          if (currentFruit.animationScale >= currentFruit.targetScale) {
            currentFruit.animationScale = currentFruit.targetScale;
            currentFruit.isBouncing = false;
          }
          requestAnimationFrame(animateGrowth);
        }
      };
      animateGrowth();
    }

    return body;
  };
  // Drop a fruit
  const dropFruit = () => {
    if (!canDrop.current || !worldRef.current || isGameOver) return;
    
    canDrop.current = false;
    const fruit = FRUITS[nextFruit];
    
    const fruitBody = createFruit(dropPosition, 100, fruit, worldRef.current);
    
    // Add a small random horizontal impulse for more dynamic movement
    if (fruitBody) {
      const randomImpulse = (Math.random() - 0.5) * 0.001; // Small random horizontal force
      Matter.Body.applyForce(fruitBody, fruitBody.position, { x: randomImpulse, y: 0 });
    }

    // Generate next fruit (only first 3 types can be dropped: Cherry, Strawberry, Lemon)
    setNextFruit(Math.floor(Math.random() * 3));
      // Check for game over (fruits above drop line)
    setTimeout(() => {
      const droppedFruits = Array.from(fruitBodies.current.entries());
      const gameOverCheck = droppedFruits.some(([id, fruit]) => {
        const body = engineRef.current.world.bodies.find(b => b.id === id);
        // Game over if fruit is 10px above the drop line (150px)
        return body && body.position.y < 140; // 150 - 10 = 140px
      });
      
      if (gameOverCheck) {
        setIsGameOver(true);
      }
      
      canDrop.current = true;
    }, 1000);
  };
  // Handle mouse/touch movement
  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    // Ensure fruits don't spawn inside walls - walls span from 0-25 and 350-375
    const minFruitRadius = 15; // Updated to match new smallest fruit size
    const leftWallEdge = 25;
    const rightWallEdge = 350;
    const clampedX = Math.max(leftWallEdge + minFruitRadius, Math.min(x, rightWallEdge - minFruitRadius));
    setDropPosition(clampedX);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    // Ensure fruits don't spawn inside walls - walls span from 0-25 and 350-375
    const minFruitRadius = 15; // Updated to match new smallest fruit size
    const leftWallEdge = 25;
    const rightWallEdge = 350;
    const clampedX = Math.max(leftWallEdge + minFruitRadius, Math.min(x, rightWallEdge - minFruitRadius));
    setDropPosition(clampedX);
  };  // New game function
  const newGame = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmNewGame = () => {
    setShowConfirmModal(false);
    
    // Reset drop capability
    canDrop.current = true;
    
    if (engineRef.current && worldRef.current) {
      // Clear all fruits
      const bodies = engineRef.current.world.bodies.filter(body => 
        fruitBodies.current.has(body.id)
      );
      if (bodies.length > 0) {
        Matter.World.remove(worldRef.current, bodies);
      }
      fruitBodies.current.clear();
    }    // Reset game state
    setScore(0);
    setNextFruit(Math.floor(Math.random() * 3));
    setIsGameOver(false);
    setDropPosition(187.5); // Center of 375px width
  };

  const handleCancelNewGame = () => {
    setShowConfirmModal(false);
  };
  // Initialize game
  useEffect(() => {
    const physics = initPhysics();
    setNextFruit(Math.floor(Math.random() * 3));
    
    // Store refs for cleanup
    const currentAnimationId = animationIdRef;
    const currentFruitBodies = fruitBodies;
    
    return () => {
      // Cancel animation frame
      if (currentAnimationId.current) {
        cancelAnimationFrame(currentAnimationId.current);
        currentAnimationId.current = null;
      }
      
      // Stop physics
      if (physics) {
        Matter.Render.stop(physics.render);
        Matter.Engine.clear(physics.engine);
      }
      
      // Clear fruit bodies
      currentFruitBodies.current.clear();
    };  }, [initPhysics]);
  return (    <div className="game-container">
      <div className="game-header">
        <div className="score-section">
          <div className="score">{score}</div>
          <div className="high-score">En Yüksek: {highScore}</div>
        </div><div className="next-fruit">
          <canvas 
            ref={(canvas) => {
              if (canvas && FRUITS[nextFruit]) {
                const ctx = canvas.getContext('2d');
                canvas.width = 60;
                canvas.height = 60;
                ctx.clearRect(0, 0, 60, 60);                const fruit = FRUITS[nextFruit];
                const previewSize = 20; // Increased preview size for better visibility
                
                ctx.save();
                ctx.translate(30, 30);
                  switch (fruit.renderer) {
                  case 'cherry':
                    drawCherry(ctx, 0, 0, previewSize);
                    break;
                  case 'strawberry':
                    drawStrawberry(ctx, 0, 0, previewSize);
                    break;
                  case 'lemon':
                    drawLemon(ctx, 0, 0, previewSize);
                    break;
                  case 'orange':
                    drawOrange(ctx, 0, 0, previewSize);
                    break;
                  case 'kiwi':
                    drawKiwi(ctx, 0, 0, previewSize);
                    break;
                  case 'pineapple':
                    drawPineapple(ctx, 0, 0, previewSize);
                    break;
                  case 'apple':
                    drawApple(ctx, 0, 0, previewSize);
                    break;
                  case 'watermelon':
                    drawWatermelon(ctx, 0, 0, previewSize);
                    break;
                  default:
                    ctx.fillStyle = fruit.color;
                    ctx.beginPath();
                    ctx.arc(0, 0, previewSize, 0, Math.PI * 2);
                    ctx.fill();
                }
                
                ctx.restore();
              }
            }}
            style={{ width: '60px', height: '60px' }}
          />        </div>
      </div>      <button className="new-game-btn" onClick={newGame}>
        <span>Yeni</span>
        <span>Oyun</span>
      </button>

      <div className="drop-line"></div>
      
      <div 
        className="drop-indicator" 
        style={{ left: dropPosition - 2 }}
      ></div>      <canvas
        ref={canvasRef}
        className="game-canvas"
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
        onClick={dropFruit}
        onTouchEnd={dropFruit}
      />

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onRequestClose={handleCancelNewGame}
        className="confirm-modal"
        overlayClassName="confirm-modal-overlay"
        ariaHideApp={false}
      >
        <div className="modal-content">
          <h3>🎮 Yeni Oyun</h3>
          <p>Gerçekten yeni oyuna başlamak istiyor musunuz?</p>
          <p className="modal-warning">Mevcut ilerlemeniz kaybolacak!</p>
          <div className="modal-buttons">
            <button className="modal-btn confirm-btn" onClick={handleConfirmNewGame}>
              Evet
            </button>
            <button className="modal-btn cancel-btn" onClick={handleCancelNewGame}>
              İptal
            </button>
          </div>
        </div>
      </Modal>

      {isGameOver && (        <div className="game-over-overlay">          <div className="game-over-content">
            <h2>🎮 Oyun Bitti!</h2>
            <p>Son Skorunuz: <strong>{score}</strong></p>
            <p>En Yüksek Skorunuz: <strong>{highScore}</strong></p>
            <p>Meyveleri birleştirerek daha yüksek skor alın!</p>
            <button className="new-game-btn" onClick={newGame}>
              Tekrar Oyna
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
